import React, { useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { Navigation, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { generateGlassesThumbImage } from 'utils/image.utils';
import VtoIcon from './Icons/VtoIcon';
import { slugToString } from 'functions/slug';
import { useVTOStore } from 'zus/vtoStore';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import { fixPrice } from 'functions/intFixPrice';
import { useRouter } from 'next/router';
const VTOModalV2 = dynamic(
    () => import('components/VTO/VTOModalV2'),
    { ssr: false }
);

interface GlassSelection {
    shape: string;
    arm_color: string;
    treatment: string;
    lens_color: string;
    frame_color: string;
    ecommerce_category: {
        slug: string;
        name: string;
        type: string;
        regular_price?: number;
        price?: number;
        categoryLensTypes?: Array<{
            type: string;
            regular_price: number;
            price: number;
        }>;
    };
}

interface TopSellersData {
    type: string;
    publishDate: string;
    publishStatus: string;
    regions: string[];
    glass_selections: GlassSelection[];
}

interface HomePageProductWidgetProps {
    data: TopSellersData;
    title: string;
    uniqueId?: string;
}

const HomePageProductWidget: React.FC<HomePageProductWidgetProps> = ({ data, title, uniqueId = title.toLowerCase().replace(/\s+/g, '-') }) => {
    const setSelectedColors = useVTOStore((state) => state.setSelectedColors);
    const updateVisibility = useVTOStore((state) => state.updateVisibility);
    const setCategory = useVTOStore((state) => state.setCategory);
    const setPrice = useVTOStore((state) => state.setPrice);
    const setCustomTitle = useVTOStore((state) => state.setCustomTitle);
    const router = useRouter();
    const [showVTOModal, setShowVTOModal] = useState(false);

    const handleVTOClick = (item: GlassSelection, e: React.MouseEvent) => {
        e.preventDefault();
        const isSunglasses = item.ecommerce_category.slug?.includes('sunglasses') ||
            item.ecommerce_category.slug?.includes('sun-glasses') ||
            item.treatment?.includes('sun-glasses');

        setCategory(item.ecommerce_category.slug);
        setSelectedColors(
            item.frame_color,
            item.arm_color || item.frame_color,
            isSunglasses ? 'grey' : 'clear',
            item.shape
        );
        setPrice(
            item.ecommerce_category.price || 0,
            item.ecommerce_category.regular_price || item.ecommerce_category.price || 0
        );
        setCustomTitle(title);
        updateVisibility(true);
        setShowVTOModal(true);
    };

    const createProductLink = (item: GlassSelection) => {
        const frameColor = item.frame_color;
        const armColor = item.arm_color;
        const shape = item.shape;

        let url = `/shop/${item.ecommerce_category.slug}/${frameColor}`;

        if (frameColor !== armColor) {
            url += `?armColor=${armColor}`;
        }

        if (shape == 'round') {
            if (url.includes('?armColor=')) {
                url += `&shape=${shape}`;
            } else {
                url += `?shape=${shape}`;
            }
        }

        return url;
    };

    const ProductCard = ({ item }: { item: GlassSelection }) => (
        <Link
            href={createProductLink(item)}
            className="group flex flex-col bg-white rounded-lg overflow-hidden"
        >
            <div className="relative">
                <div className="relative h-[390px] w-full bg-[#f6f8fa]">
                    <div className="absolute top-4 left-4 z-10 ">
                        <div className="text-[#959595] text-[14px]">From</div>
                        <div className="text-[28px] font-semibold text-[#2A2A2A]">
                            {fixPrice('$' + item.ecommerce_category.price, router.locale || 'international')}
                        </div>
                    </div>

                    <div className="absolute bottom-4 right-4 z-10 ">
                        <button
                            onClick={(e) => handleVTOClick(item, e)}
                            className="group flex items-center gap-2 text-gray-600 hover:text-[#FF8200] transition-colors bg-white rounded-[100px] px-[12px] py-[4px] shadow-[1px_1px_4px_0px_rgba(0,0,0,0.10)] text-[#616161] text-[14px]"
                        >
                            <VtoIcon fill="currentColor" />
                            Try me on
                        </button>
                    </div>
                    <Image
                        src={generateGlassesThumbImage(item.frame_color, item.arm_color, '0', item.treatment === 'sun-glasses-grey' ? 'grey' : 'clear', item.shape)}
                        alt={item.ecommerce_category.name}
                        width={390}
                        height={390}
                        className="w-full h-full object-contain"
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        quality={90}
                    />
                </div>
            </div>
            <div className="flex items-center flex-col mt-[10px]">
                <h3 className="text-[16px] font-semibold text-[#2A2A2A] mb-[6px]">
                    {item.ecommerce_category.name}
                </h3>
                <p className="text-[#959595] text-[16px]">
                    {item.frame_color === item.arm_color
                        ? slugToString(item.frame_color)
                        : `${slugToString(item.frame_color)} Frame - ${slugToString(item.arm_color)} Arm`
                    }
                </p>
            </div>
        </Link>
    );

    if (!data || !data.glass_selections) {
        return null;
    }

    return (
        <>
            <div className="mx-auto max-w-[1224px] mt-[40px] lg:px-4 w-full mb-[70px]">
                <h2 className="text-[36px] font-semibold text-[#2A2A2A] text-center mb-[32px]">{title}</h2>

                {/* Mobile Slider */}
                <div className="md:hidden">
                    <Swiper
                        className={`home-page-slider-${uniqueId}`}
                        modules={[Pagination]}
                        loop={false}
                        speed={600}
                        slidesPerView={1}
                        spaceBetween={16}
                        watchOverflow={true}
                        allowTouchMove={true}
                        simulateTouch={true}
                        pagination={{
                            clickable: true,
                            el: `[data-home-page-slider-pagination="${uniqueId}"]`,
                            bulletClass: `slider-bullet-${uniqueId}`,
                            bulletActiveClass: '!bg-brand-black100',
                            horizontalClass: `slider-pagination-horizontal-${uniqueId}`,
                            renderBullet: (_, className) => {
                                return `<span class="${className} cursor-pointer transition-all duration-200 h-[10px] w-[10px] !m-[4px] rounded-full bg-brand-grey500"></span>`;
                            }
                        }}
                    >
                        {data && data.glass_selections && data.glass_selections.map((item, index) => (
                            <SwiperSlide key={index} className="!h-auto">
                                <div className="h-full px-[10px]">
                                    <ProductCard item={item} />
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                    <div
                        data-home-page-slider-pagination={uniqueId}
                        className="mx-auto mt-[30px] flex flex-wrap justify-center"
                    ></div>
                </div>

                {/* Desktop Grid */}
                <div className="hidden md:grid md:grid-cols-3 gap-[24px]">
                    {data && data.glass_selections && data.glass_selections.map((item, index) => (
                        <ProductCard key={index} item={item} />
                    ))}
                </div>
            </div>

            {showVTOModal && (
                <VTOModalV2 showAllColors={true} />
            )}
        </>
    );
};

export default HomePageProductWidget; 